// react
import React from 'react';
import styled, { css } from 'styled-components';

// theme
import { theme } from '@/theme';

interface CardProps {
  className?: string;
  elevation?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  children?: React.ReactNode;
  color?: keyof typeof theme.color.cards | 'none';
  isPaddingSm?: boolean;
  isBordered?: boolean;
  isNoPadding?: boolean;
  isTransparent?: boolean;
  isFullWidth?: boolean;
  as?: React.ElementType;
}

/**
 * Card is a container for content.
 */
export const Card = ({
  className,
  elevation = 2,
  children,
  color = 'white',
  isFullWidth = false,
  isPaddingSm = false,
  isNoPadding = false,
  isTransparent = false,
  isBordered = true,
  as = 'div',
}: CardProps) => {
  return (
    <StyledCard
      className={className}
      $elevation={elevation}
      $isPaddingSm={isPaddingSm}
      $isNoPadding={isNoPadding}
      $isBordered={isBordered}
      $isTransparent={isTransparent}
      $isFullWidth={isFullWidth}
      $color={color}
      as={as}
    >
      {children}
    </StyledCard>
  );
};

interface StyledCardProps {
  $elevation: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  $isPaddingSm: boolean;
  $color: keyof typeof theme.color.cards;
  $isBordered: boolean;
  $isNoPadding: boolean;
  $isTransparent: boolean;
  $isFullWidth: boolean;
}

const StyledCard = styled.div<StyledCardProps>`
  background: ${(props) => props.theme.color.neutral[10]};
  border-radius: 8px;
  box-shadow: ${(props) => props.theme.elevation[props.$elevation]};
  border: ${(props) =>
    props.$isBordered && css`1px solid ${props.theme.color.neutral[50]}`};
  box-sizing: border-box;
  text-decoration: none;
  width: ${(props) => (props.$isFullWidth ? '100%' : 'auto')};

  padding: ${(props) => props.theme.spacing[36]};
  ${(props) =>
    props.$isPaddingSm &&
    css`
      padding: ${props.theme.spacing[16]};
    `};

  ${(props) =>
    props.$color !== 'white' &&
    props.$color !== 'none' &&
    css`
      background: ${props.theme.color.cards[props.$color]
          .primary}${props.$isTransparent ? 'F2' : 'FF'};
      border: ${props.$isBordered
        ? `1px solid ${props.theme.color.cards[props.$color].secondary}`
        : 'none'};
      color: ${props.$isBordered &&
      `${props.theme.color.cards[props.$color].tertiary}`};
    `}

  ${(props) =>
    props.$color === 'none' &&
    css`
      background: none;
    `}
  ${(props) =>
    props.$color === 'white' &&
    css`
      border: ${props.$isBordered
        ? `1px solid ${props.theme.color.cards.green.secondary}`
        : 'none'};
      background: ${props.theme.color.neutral[20]};
      color: ${props.theme.color.neutral[100]};
    `}
  ${(props) =>
    props.$color === 'green' &&
    css`
      background: ${props.theme.color.cards.green.secondary};
      color: ${props.theme.color.neutral[100]};
    `}
  ${(props) =>
    props.$color === 'yellow' &&
    css`
      border: ${props.$isBordered
        ? `1px solid ${props.theme.color.cards.green.secondary}`
        : 'none'};
      background: ${(props) => props.theme.color.cards.buttermilk.primary};
      color: ${props.theme.color.neutral[100]};
    `}
  ${(props) =>
    props.$isNoPadding &&
    css`
      padding: 0;
    `}
`;

Card.displayName = 'Card';

export default Card;
